import { Component, inject, OnInit, output, signal, input, computed, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ITabs } from '@devinforius/i-navigation';
import { ActionStatus, ActionTarget, CreateAction } from '@shared/interfaces/actions';
import { Contact } from '@shared/interfaces/contacts';
import { ActionKindDto } from '@shared/interfaces/dtos/actions';
import { GroupDto } from '@shared/interfaces/dtos/groups';
import { ServiceDto } from '@shared/interfaces/dtos/services';
import { UserDto } from '@shared/interfaces/dtos/users';
import { ActionsService } from '@shared/services/actions.service';
import { ContactsService } from '@shared/services/contacts.service';
import { ButtonsGroup } from '@shared/types/dev';
import { CheckableItem } from '@shared/types/dtos';
import { FormValidators, ScheduleUtil } from '@shared/utils';
import { finalize, forkJoin, of } from 'rxjs';
import { FormOptions } from 'src/app/common/models/form-options';
import { NotificationSuccess } from 'src/app/core/models';
import { Notifier } from 'src/app/core/services';
import { ApplicationConfiguration } from 'src/app/core/services/application-configuration';

@Component({
  selector: 'idel-add-action-form',
  templateUrl: './add-action-form.component.html',
  styleUrl: './add-action-form.component.scss',
})
export class AddActionFormComponent implements OnInit, OnDestroy {
  private readonly service = inject(ActionsService);
  private readonly appConfig = inject(ApplicationConfiguration);
  private readonly contactsService = inject(ContactsService);
  private readonly formOptions = inject(FormOptions);
  private readonly notifier = inject(Notifier);

  public readonly target = input.required<ActionTarget>();

  public readonly onCancel = output<void>();
  public readonly onCreate = output<void>();

  public readonly actionKindsOptions = this.formOptions.actionKinds();
  public usersOptions = this.formOptions.users();
  public groupsOptions = this.formOptions.groups();
  public servicesOptions = this.formOptions.services();
  private allUsersOptions: CheckableItem<UserDto>[] = [];
  private allContactsOptions: CheckableItem<Contact>[] = [];
  public actionStatusesOptions = signal<ActionStatus[]>([]);

  public readonly canChooseAStatus = computed(() => !this.appConfig.config().delibConfig.hideStatutAction);
  public readonly activeRecipientTab = signal<'users' | 'contacts' | 'groups' | 'services'>('users');
  public readonly recipientsTabs = signal<ITabs[]>([
    {
      header: 'Utilisateurs',
      id: 'users',
      active: true,
      count: this.usersOptions.length,
    },

    {
      header: 'Groupes',
      id: 'groups',
      count: this.groupsOptions.length,
    },
    {
      header: 'Services',
      id: 'services',
      count: this.servicesOptions.length,
    },
  ]);
  public readonly recipientsStatesButtonsGroup: ButtonsGroup = [
    {
      label: 'Tous',
      value: 'all',
      command: () => {
        this.filterUsersOrContactsByActivityState('all');
      },
    },
    {
      label: 'Actifs',
      value: 'active',
      command: () => {
        this.filterUsersOrContactsByActivityState('active');
      },
    },
    {
      label: 'Inactifs',
      value: 'inactive',
      command: () => {
        this.filterUsersOrContactsByActivityState('inactive');
      },
    },
  ];
  public readonly addActionForm = new FormGroup({
    action: new FormControl<ActionKindDto | null>(null, Validators.required),
    message: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(5),
      FormValidators.notEmptyString(),
    ]),
    recipients: new FormGroup(
      {
        users: new FormControl<UserDto[]>([]),
        contacts: new FormControl<Contact[]>([]),
        groups: new FormControl<GroupDto[]>([]),
        services: new FormControl<ServiceDto[]>([]),
      },
      FormValidators.atLeastOneNotNull()
    ),
    deadline: new FormControl<Date | null>(null, FormValidators.inFuture(true)),
  });
  public readonly creatingAction = signal(false);

  public get noRecipientsSelected(): boolean {
    const form = this.addActionForm.controls.recipients as FormGroup;

    return !!form?.errors?.atLeastOneNotNull;
  }

  ngOnInit(): void {
    this.initRecipientsOptions();
    this.addStatusFormControlIfNeeded();
  }

  ngOnDestroy(): void {
    this.resetForm();
  }

  private resetForm(): void {
    this.addActionForm.reset({
      action: null,
      message: '',
      recipients: {
        users: [],
        contacts: [],
        groups: [],
        services: [],
      },
      deadline: null,
    });
  }

  private addStatusFormControlIfNeeded(): void {
    const canChooseAStatus = this.canChooseAStatus();

    if (!canChooseAStatus) return;

    (this.addActionForm as FormGroup).addControl('status', new FormControl<string | null>(null));
  }

  private initRecipientsOptions(): void {
    forkJoin({
      users: of(this.formOptions.users().filter((user) => user.active)),
      groups: of(this.formOptions.groups()),
      services: of(this.formOptions.services()),
      actionStatuses: this.service.fetchActionStatuses(),
    }).subscribe(({ users, groups, services, actionStatuses }) => {
      this.allUsersOptions = this.addCheckKey(users);
      this.usersOptions = [...this.allUsersOptions];
      this.groupsOptions = this.addCheckKey(groups);
      this.servicesOptions = this.addCheckKey(services);
      this.actionStatusesOptions.set(actionStatuses);

      this.setRecipientsTabsCounters();
      this.saveRecipientsOptionsIntoSessionStorage();
    });
  }

  private addCheckKey<T extends object>(items: T[]): (T & { checked: boolean })[] {
    return items.map((item) => ({ ...item, checked: 'checked' in item ? (item.checked as boolean) : false }));
  }

  private saveRecipientsOptionsIntoSessionStorage(): void {
    sessionStorage.setItem('recipients-users', JSON.stringify(this.allUsersOptions));
    sessionStorage.setItem('recipients-groups', JSON.stringify(this.groupsOptions));
    sessionStorage.setItem('recipients-services', JSON.stringify(this.servicesOptions));
  }

  private setRecipientsTabsCounters(): void {
    this.recipientsTabs.set([
      {
        header: 'Utilisateurs',
        id: 'users',
        active: true,
        count: this.usersOptions.length,
      },

      {
        header: 'Groupes',
        id: 'groups',
        count: this.groupsOptions.length,
      },
      {
        header: 'Services',
        id: 'services',
        count: this.servicesOptions.length,
      },
    ]);
  }

  public onActiveTabChange(tabId: 'users' | 'contacts' | 'groups' | 'services'): void {
    this.activeRecipientTab.set(tabId);
  }

  private filterUsersOrContactsByActivityState(state: 'all' | 'active' | 'inactive'): void {
    switch (this.activeRecipientTab()) {
      case 'users': {
        this.usersOptions =
          state === 'all'
            ? [...this.allUsersOptions]
            : this.allUsersOptions.filter((user) => user.active === (state === 'active'));

        break;
      }
    }
  }

  public onRecipientCheck(checkedRecipients: CheckableItem<UserDto | Contact | GroupDto | ServiceDto>[]): void {
    const form = this.addActionForm.controls.recipients as FormGroup;

    switch (this.activeRecipientTab()) {
      case 'users': {
        const selectedUsers = this.allUsersOptions.filter((user) => user.checked);
        form.controls.users.setValue(selectedUsers);
        sessionStorage.setItem('recipients-users', JSON.stringify(this.allUsersOptions));
        break;
      }
      case 'contacts': {
        const selectedContacts = this.allContactsOptions.filter((contact) => contact.checked);
        form.controls.contacts.setValue(selectedContacts);
        sessionStorage.setItem('recipients-contacts', JSON.stringify(this.allContactsOptions));
        break;
      }
      case 'groups': {
        const selectedGroups = checkedRecipients.filter((recipient) => recipient.checked) as CheckableItem<GroupDto>[];
        form.controls.groups.setValue(selectedGroups);
        break;
      }
      case 'services': {
        const selectedServices = checkedRecipients.filter(
          (recipient) => recipient.checked
        ) as CheckableItem<ServiceDto>[];
        form.controls.services.setValue(selectedServices);
        break;
      }
    }

    this.saveRecipientsOptionsIntoSessionStorage();
  }

  public onCancelClick(): void {
    this.addActionForm.reset();
    this.onCancel.emit();
  }

  public onCreateActionClick(): void {
    if (this.addActionForm.invalid || this.creatingAction()) return;

    this.creatingAction.set(true);

    const formValue = this.addActionForm.value;
    const recipients = formValue.recipients;

    const dto: CreateAction = {
      action: formValue.action!.description!,
      message: formValue.message!,
      echeance: formValue?.deadline ? ScheduleUtil.toHttpRequestFormat(formValue.deadline) : null,
      accuseDeReponse: false,
      contacts: [
        ...(recipients?.users?.length ? recipients.users : []).map((user) => ({
          contactIdiAdmin: user.contactForUser.contactId,
          contactUserId: user.userId,
        })),
      ],
      groupeIds: (recipients?.groups ?? []).map((group) => group.id),
      serviceIds: (recipients?.services ?? []).map((service) => service.id),
      statut: ((formValue as FormGroup)?.status as unknown as ActionKindDto)?.id ?? null,
    };

    this.service
      .insertOneAction(this.target(), dto)
      .pipe(finalize(() => this.creatingAction.set(false)))
      .subscribe({
        next: () => {
          this.notifier.showSuccess(new NotificationSuccess({ message: 'Action créée.' }));
          this.resetForm();
          this.onCreate.emit();
        },
      });
  }
}
